/**
 * The date filter every wallet report shares.
 *
 * One component so "Last 7 days" cannot mean two different windows on two
 * screens. The presets map to server-side windows (see the backend's
 * resolveDateRange) rather than being computed here - the client and the server
 * disagreeing about where a day starts is exactly the kind of bug that makes a
 * reconciliation report untrustworthy.
 */
import { Field, SegmentedControl, TextInput } from '../../features/b2b/components/primitives'
import type { DateRangeFilter, DateRangePreset } from '../../services/walletApi'

const PRESETS: Array<{ value: DateRangePreset; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom' },
]

export default function DateRangePicker({
  value,
  onChange,
}: {
  value: DateRangeFilter
  onChange: (next: DateRangeFilter) => void
}) {
  const preset = value.range ?? 'last_30_days'

  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <SegmentedControl<DateRangePreset>
        value={preset}
        options={PRESETS}
        size="sm"
        onChange={(next) =>
          // Switching AWAY from custom must drop from/to, or the server would
          // see both a preset and a window and silently prefer one.
          onChange(next === 'custom' ? { range: 'custom', from: value.from, to: value.to } : { range: next })
        }
      />

      {preset === 'custom' ? (
        <>
          <Field label="From">
            <TextInput
              type="date"
              value={value.from ?? ''}
              onChange={(event) => onChange({ ...value, range: 'custom', from: event.target.value })}
              style={{ padding: '7px 10px' }}
            />
          </Field>
          <Field label="To">
            <TextInput
              type="date"
              value={value.to ?? ''}
              onChange={(event) => onChange({ ...value, range: 'custom', to: event.target.value })}
              style={{ padding: '7px 10px' }}
            />
          </Field>
        </>
      ) : null}
    </div>
  )
}
